import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/site/Header'
import { Footer } from '@/components/site/Footer'

export const Route = createFileRoute('/unsubscribe')({
  head: () => ({
    meta: [
      { title: 'Unsubscribe — Peopels' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: UnsubscribePage,
})

type State = 'loading' | 'valid' | 'invalid' | 'already' | 'success' | 'error'

function UnsubscribePage() {
  const [state, setState] = useState<State>('loading')
  const [submitting, setSubmitting] = useState(false)
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const t = new URL(window.location.href).searchParams.get('token')
    setToken(t)
    if (!t) { setState('invalid'); return }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok) { setState('invalid'); return }
        if (data.valid === false && data.reason === 'already_unsubscribed') { setState('already'); return }
        if (data.valid) { setState('valid'); return }
        setState('invalid')
      })
      .catch(() => setState('error'))
  }, [])

  async function confirm() {
    if (!token) return
    setSubmitting(true)
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await r.json().catch(() => ({}))
      if (data.success) setState('success')
      else if (data.reason === 'already_unsubscribed') setState('already')
      else setState('error')
    } catch {
      setState('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-md flex-1 px-4 py-16">
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          {state === 'loading' && <p className="text-sm text-muted-foreground">Checking your link…</p>}
          {state === 'valid' && (
            <>
              <h1 className="font-display text-2xl font-bold">Unsubscribe from Peopels emails</h1>
              <p className="mt-3 text-sm text-muted-foreground">You'll stop receiving marketing and notification emails from Peopels. Booking-critical messages may still be sent for active jobs.</p>
              <Button className="mt-6 w-full" onClick={confirm} disabled={submitting}>{submitting ? 'Working…' : 'Confirm unsubscribe'}</Button>
            </>
          )}
          {state === 'success' && (
            <>
              <h1 className="font-display text-2xl font-bold">You're unsubscribed</h1>
              <p className="mt-3 text-sm text-muted-foreground">We've removed you from our email list. Sorry to see you go.</p>
            </>
          )}
          {state === 'already' && (
            <>
              <h1 className="font-display text-2xl font-bold">Already unsubscribed</h1>
              <p className="mt-3 text-sm text-muted-foreground">This email address is no longer on our list.</p>
            </>
          )}
          {state === 'invalid' && (
            <>
              <h1 className="font-display text-2xl font-bold">Link is invalid or expired</h1>
              <p className="mt-3 text-sm text-muted-foreground">Please use the unsubscribe link from a recent email.</p>
            </>
          )}
          {state === 'error' && (
            <>
              <h1 className="font-display text-2xl font-bold">Something went wrong</h1>
              <p className="mt-3 text-sm text-muted-foreground">Please try again in a moment.</p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}
